import { useBeforeLeave, useIsRouting, useLocation, useNavigate } from "@solidjs/router"
import { createEffect, untrack } from "solid-js"
import { createStore, reconcile, unwrap } from "solid-js/store"
import { applyPath, backPath, forwardPath, type HistoryNavigation, type TitlebarHistory } from "./history"

export function createTitlebarHistory() {
  const location = useLocation()
  const navigate = useNavigate()
  const routing = useIsRouting()
  const [state, setState] = createStore<TitlebarHistory>({ stack: [], index: 0, action: undefined })
  let pending: { navigation: HistoryNavigation; prevented?: () => boolean } | undefined

  useBeforeLeave((event) => {
    if (pending?.prevented?.()) pending = undefined
    // The router commits a redirect chain with the first navigation's replace
    // option, even when a later redirect supplies a different option.
    if (pending) return
    pending = {
      navigation:
        typeof event.to === "number" ? { index: state.index + event.to } : event.options?.replace ? "replace" : "push",
      prevented: () => event.defaultPrevented,
    }
  })

  createEffect(() => {
    const current = { url: `${location.pathname}${location.search}${location.hash}`, state: location.state }
    if (routing()) return
    untrack(() => {
      const navigation = pending?.prevented?.() ? undefined : pending?.navigation
      pending = undefined
      setState(reconcile(applyPath(unwrap(state), current, navigation)))
    })
  })

  function move(direction: "back" | "forward") {
    if (routing()) return
    const next = direction === "back" ? backPath(state) : forwardPath(state)
    if (!next) return
    pending = { navigation: { index: next.state.index } }
    navigate(next.to.url, { state: unwrap(next.to.state) })
  }

  return {
    state,
    back: () => move("back"),
    forward: () => move("forward"),
  }
}
